const { localeCountry } = window.afdcStationsOptions;

const globalDefaults = {
  country: window.afdcStationsOptions.country || 'all',
  fuel: ['all'],
  access: ['public'],
  status: ['E'],
  owner: 'all',
  payment: 'all',
  radius: localeCountry === 'CA' ? 10 : 5,
  radius_unit: localeCountry === 'CA' ? 'km' : 'mi',
  distance: localeCountry === 'CA' ? 10 : 5,
  distance_unit: localeCountry === 'CA' ? 'km' : 'mi',
  page: 1,
  cng_fill_type: 'all',
  cng_vehicle_class: 'all',
  cng_psis: ['all'],
  ev_levels: ['2', 'dc_fast'],
  ev_connectors: ['all'],
  ev_networks: ['all'],
  e85_blends: false,
  lng_vehicle_class: 'all',
  hy_nonretail: false,
  lpg_secondary: false,
};

const corridorDefaults = {
  ...globalDefaults,
  country: 'all',
  fuel: ['ELEC'],
  ev_levels: ['dc_fast'],
  ev_connectors: [
    'CHADEMO',
    'J1772COMBO',
  ],
  ev_connector_type_operator: 'AND',
  cng_fill_type: 'Q',
  cng_psis: ['3600'],
};

const filterDefaults = {
  FindNearestStations: globalDefaults,
  FindRouteStations: globalDefaults,
  CorridorStations: corridorDefaults,
  AnalyzeStations: {
    ...globalDefaults,
    location_mode: 'region',
    region: 'all',
  },
};

const globalTypes = {
  e85_blends: 'boolean',
  hy_nonretail: 'boolean',
  lpg_secondary: 'boolean',
  radius: 'integer',
  distance: 'integer',
  page: 'integer',
  fuel: 'array',
  access: 'array',
  status: 'array',
  ev_levels: 'array',
  ev_connectors: 'array',
  ev_networks: 'array',
  cng_psis: 'array',
};

const filterTypes = {
  FindNearestStations: globalTypes,
  FindRouteStations: globalTypes,
  AnalyzeStations: globalTypes,
  CorridorStations: globalTypes,
};

const moduleState = {
  filters: {
    FindNearestStations: {},
    FindRouteStations: {},
    AnalyzeStations: {},
    CorridorStations: {},
  },

  scopeSpecificFilterNames: {
    FindNearestStations: [
      'location',
      'page',
    ],
    FindRouteStations: [
      'start',
      'end',
      'distance',
      'distance_unit',
      'page',
    ],
    AnalyzeStations: [],
    CorridorStations: [
      'start',
      'end',
      'location',
    ],
  },
};

const moduleGetters = {
  // Getters for the filters hash.
  getFiltersAll: (state) => state.filters,
  getFiltersForScope: (state) => (scope) => state.filters[scope],
  getFiltersFindNearest: (state) => state.filters.FindNearestStations,
  getFiltersFindRoute: (state) => state.filters.FindRouteStations,
  getFiltersAnalyze: (state) => state.filters.AnalyzeStations,
  getFiltersCorridors: (state) => state.filters.CorridorStations,
  // Getters for the defaults.
  getFilterDefaultsForScope: () => (scope) => filterDefaults[scope],

  // Getter for the filters hash with default values merged in.
  getFiltersForScopeWithDefaults: (state) => (scope) => ({
    ...filterDefaults[scope],
    ...state.filters[scope],
  }),

  // Getters for individual filter values and their default values.
  getFilterDefaultValueInScope: () => (scope, name) => filterDefaults[scope][name],
  getFilterValueInScope: (state) => (scope, name) => state.filters[scope][name],
  getFilterValueInScopeWithDefault: (state) => (scope, name) => {
    if (state.filters[scope][name] !== undefined) {
      return state.filters[scope][name];
    }
    return filterDefaults[scope][name];
  },

  // Getter shortcuts for filters in whatever the current URL's scope is (so
  // when viewing nearest stations, all of the nearest filters are simply
  // available as getFilters instead of needing to explicitly specify the
  // scope).
  getFilters: (state, getters) => {
    const routeName = getters.getNavActiveFilterRouteName;
    return getters.getFiltersForScope(routeName);
  },
  getFilterDefaults: (state, getters) => {
    const routeName = getters.getNavActiveFilterRouteName;
    return getters.getFilterDefaultsForScope(routeName);
  },
  getFiltersWithDefaults: (state, getters) => {
    const routeName = getters.getNavActiveFilterRouteName;
    return getters.getFiltersForScopeWithDefaults(routeName);
  },
  getFilterDefaultValue: (state, getters) => (name) => {
    const routeName = getters.getNavActiveFilterRouteName;
    return getters.getFilterDefaultValueInScope(routeName, name);
  },
  getFilterValue: (state, getters) => (name) => {
    const routeName = getters.getNavActiveFilterRouteName;
    return getters.getFilterValueInScope(routeName, name);
  },
  getFilterValueWithDefault: (state, getters) => (name) => {
    const routeName = getters.getNavActiveFilterRouteName;
    return getters.getFilterValueInScopeWithDefault(routeName, name);
  },

  getCountry: (state, getters) => getters.getFilterValueWithDefault('country'),

  getFilterNamesSpecificToScope: (state) => (scope) => state.scopeSpecificFilterNames[scope],
};

// Type-cast the filter query parameters so we always have consistent types.
//
// This ensures boolean values from checkboxes are still treated as
// booleans when coming from the query params on initial load (rather than
// a string of "true"), so we don't have to manually specify true-value and
// false-value everywhere.
//
// This also addresses some issues with the array fields:
//
// 1. Ensuring the value is always an array, including on initial load when
//    vue's default query param parser may not be able to determine if the
//    item should be an array (if just 1 item is present).
// 2. Gives us a way to handle un-selecting all the values in an array
//    field (which should typically result in no results). We'll serialize
//    this in the query string as a null value (eg, "?ev_levels"), but then
//    parse this back to an empty array here.
function typecastValue(scope, key, value) {
  let typedValue;

  switch (filterTypes[scope][key]) {
    case 'boolean':
      switch (value) {
        case true:
        case 'true':
          typedValue = true;
          break;
        default:
          typedValue = false;
          break;
      }
      break;
    case 'array':
      // Handle the special case of null representing all items being
      // unchecked for array types (since Vue's router doesn't have a way
      // to serialize an empty array).
      if (value === null) {
        typedValue = [];
      } else if (Array.isArray(value)) {
        typedValue = value;
      } else {
        typedValue = [value];
      }
      break;
    case 'integer':
      if (value === null || value === undefined || value === '') {
        typedValue = null;
      } else {
        typedValue = parseInt(value, 10);
      }
      break;
    default:
      typedValue = value;
      break;
  }

  return typedValue;
}

const mutations = {
  setFilters(state, payload) {
    const { scope } = payload;
    const { filters } = payload;

    const typedFilters = { ...filters };

    Object.keys(typedFilters).forEach((key) => {
      typedFilters[key] = typecastValue(scope, key, typedFilters[key]);
    });

    state.filters[scope] = typedFilters;
  },

  mergeFilters(state, payload) {
    const { scope } = payload;
    const { filters } = payload;

    Object.keys(filters).forEach((key) => {
      const value = filters[key];
      if (value === undefined) {
        delete state.filters[scope][key];
      } else {
        state.filters[scope][key] = typecastValue(scope, key, value);
      }
    });
  },
};

export default {
  state: moduleState,
  getters: moduleGetters,
  mutations,
};
